import { useCallback } from 'react'

import { useMutation } from '@apollo/client/react'
import type { Reference, StoreObject } from '@apollo/client'

import { toast } from '@buffer-mono/popcorn'

import { graphql } from '~publish/graphql'
import { logError } from '~publish/legacy/utils/logError'

export const DeleteChannelGroupMutation = graphql(/* GraphQL */ `
  mutation deleteChannelGroup($input: DeleteChannelGroupInput!) {
    deleteChannelGroup(input: $input) {
      __typename
      ... on DeleteChannelGroupSuccess {
        id
      }
      ... on UnauthorizedError {
        message
      }
      ... on UnexpectedError {
        message
      }
      ... on NotFoundError {
        message
      }
    }
  }
`)

interface UseDeleteChannelGroupReturn {
  deleteChannelGroup: ({
    channelGroupId,
  }: {
    channelGroupId: string
  }) => Promise<void>
  loading: boolean
}

/**
 * Custom hook to delete a channel group.
 * @returns {UseDeleteChannelGroupReturn} The function to trigger deleting a channel group and the loading state.
 */
export const useDeleteChannelGroup = (): UseDeleteChannelGroupReturn => {
  const [executeDeleteChannelGroup, { loading }] = useMutation(
    DeleteChannelGroupMutation,
  )

  const deleteChannelGroup = useCallback(
    async ({ channelGroupId }: { channelGroupId: string }) => {
      if (!channelGroupId) {
        throw new Error('Channel group ID is required')
      }

      try {
        const { data, error } = await executeDeleteChannelGroup({
          variables: {
            input: {
              id: channelGroupId,
            },
          },
          update: (cache) => {
            cache.modify({
              fields: {
                channelGroups(
                  existingChannelGroupsRef: Reference | StoreObject,
                  { readField },
                ) {
                  if (
                    existingChannelGroupsRef &&
                    'channelGroups' in existingChannelGroupsRef
                  ) {
                    const channelGroupsArray =
                      existingChannelGroupsRef.channelGroups
                    if (Array.isArray(channelGroupsArray)) {
                      return {
                        ...existingChannelGroupsRef,
                        channelGroups: channelGroupsArray.filter(
                          (channelGroupRef: Reference | StoreObject) =>
                            readField('id', channelGroupRef) !== channelGroupId,
                        ),
                      }
                    }
                  }
                  return existingChannelGroupsRef
                },
              },
            })
          },
        })

        if (data?.deleteChannelGroup) {
          toast.success('Channel group deleted successfully!')
        }

        if (error) {
          toast.error('Failed to delete channel group')
          const unexpectedError = new Error(
            error.message || 'Unexpected API response',
          )
          logError(unexpectedError, { metaData: { data, error } })
        }
      } catch (e) {
        toast.error('Failed to delete channel group')
        logError(e instanceof Error ? e : new Error(String(e)), {
          metaData: { variables: { channelGroupId } },
        })
      }
    },
    [executeDeleteChannelGroup],
  )

  return {
    deleteChannelGroup,
    loading,
  }
}
